import { useState } from 'react';
import {
  useCreatePaymentIntent,
  type FishDetails,
} from './useCreatePaymentIntent';
import { useConfirmPaymentIntent } from './useConfirmPaymentIntent';
import service from '../service';
import type {
  PaymentMethodData,
  OrderResponse,
  Order,
  ToastPayment,
} from '@Types/toast';
import {
  fetchManualChargeMenu,
  buildManualChargeOrder,
} from '../utils/toast/manualChargeHelpers';

export type ManualChargeVariables = {
  fishDetails: FishDetails;
  selectedCard: PaymentMethodData;
  cardFishId: string;
  amount: number;
  description: string;
  restaurantGuid: string;
  menuGuid: string;
  menuGroupGuid: string;
  menuItemGuid: string;
  diningOptionGuid: string;
};

export type UseManualChargeResult = {
  chargeCard: (vars: ManualChargeVariables) => Promise<OrderResponse>;
  isCharging: boolean;
  error: Error | null;
};

export function useManualCharge(): UseManualChargeResult {
  const [isCharging, setIsCharging] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createPaymentIntentMutation = useCreatePaymentIntent();
  const confirmPaymentMutation = useConfirmPaymentIntent();

  const chargeCard = async (
    vars: ManualChargeVariables,
  ): Promise<OrderResponse> => {
    setIsCharging(true);
    setError(null);

    try {
      const {
        fishDetails,
        selectedCard,
        cardFishId,
        amount,
        description,
        restaurantGuid,
        menuGuid,
        menuGroupGuid,
        menuItemGuid,
        diningOptionGuid,
      } = vars;

      const { menuGroup, menuItem } = await fetchManualChargeMenu({
        restaurantGuid,
        menuGuid,
        menuGroupGuid,
        menuItemGuid,
      });

      const order = buildManualChargeOrder({
        menuGroup,
        menuItem,
        amount,
        diningOptionGuid,
      });

      const paymentIntentRes = await createPaymentIntentMutation.mutateAsync({
        fishDetails,
        order,
        productDescription: description,
        restaurantGuid,
        cardFishId,
      });

      const confirmedPayment = await confirmPaymentMutation.mutateAsync({
        restaurantGuid,
        paymentIntentId: paymentIntentRes.paymentIntent.id,
        sessionSecret: paymentIntentRes.paymentIntent.sessionSecret,
        cardId: selectedCard.id,
      });

      const payment: ToastPayment = {
        guid: confirmedPayment.externalReferenceId,
        type: 'CREDIT',
        amount: confirmedPayment.amount / 100,
        tipAmount: 0,
      };

      const orderWithPayment: Order = {
        ...order,
        checks: [
          {
            ...order.checks[0],
            payments: [payment],
          },
        ],
      };

      const orderResult = await service.postOrder(
        orderWithPayment,
        paymentIntentRes.purchaseId,
        restaurantGuid,
      );

      if (!orderResult?.guid) {
        throw new Error('Order creation failed - no order GUID returned');
      }

      return orderResult;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      throw error;
    } finally {
      setIsCharging(false);
    }
  };

  return {
    chargeCard,
    isCharging,
    error,
  };
}
